/**
 * RR7 route tree for the legacy 33 Next page-router pages.
 *
 * Every page is `lazy()`-imported so the initial client bundle stays
 * small. Naming convention:
 *   - File: src/pages/<path>.tsx
 *   - URL: derived directly from the filesystem path; [param] becomes
 *     RR7's :param.
 *
 * SSR data loaders are stubbed for now — Phase 4 migrates the
 * `getServerSideProps` functions into RR7 `loader`s in a follow-up
 * pass. The components fetch most of their data via TanStack Query
 * client-side, which keeps the first paint working without SSR data
 * during the transition.
 */
import { type ComponentType, type ReactElement, lazy } from 'react';
import {
  Navigate,
  type LoaderFunctionArgs,
  type RouteObject,
  useLoaderData,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';

import App from './App';

// One-shot helper to keep the route table readable. The lazy import
// signature is intentionally loose — many legacy pages declare required
// props that used to come from `getServerSideProps`. RR7 doesn't pass
// those, so this wrapper injects RR7's URL params + search-string values
// as props before rendering the page (matching the legacy contract for
// `id`, `pathname`, etc. that pages-router code reads off pageProps).
function page(loader: () => Promise<unknown>) {
  const Lazy = lazy(async () => {
    const mod = (await loader()) as { default: ComponentType<unknown> };
    const Original = mod.default;
    function Wrapped(props: Record<string, unknown>) {
      const params = useParams();
      const [searchParams] = useSearchParams();
      // Routes that declare a `loader` get its return value merged in
      // as props — the legacy pages-router contract used `pageProps`,
      // RR7's loader is the closest analogue. Routes without a loader
      // get `undefined` here, which spreads to nothing.
      const loaderData = useLoaderData() as
        | Record<string, unknown>
        | undefined
        | null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const injected: Record<string, any> = {
        ...props,
        ...(loaderData && typeof loaderData === 'object' ? loaderData : {}),
      };
      searchParams.forEach((value, key) => {
        const existing = injected[key];
        if (existing === undefined) injected[key] = value;
        else if (Array.isArray(existing)) (existing as string[]).push(value);
        else injected[key] = [existing as string, value];
      });
      Object.entries(params).forEach(([key, value]) => {
        if (typeof value === 'string') injected[key] = value;
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return <Original {...(injected as any)} />;
    }
    return { default: Wrapped as unknown as ComponentType<unknown> };
  });
  return <Lazy />;
}

// Loader for `/anime/:id` — hits the api's own `/api/anime/:id` route so
// the page receives `initialData` during SSR. Without this, the page
// component starts in loading state during render, never mounts its
// `<SeoHead>`, and crawlers only see the static index.html OG defaults
// (the entire reason Discord was scraping the home brand OG for every
// anime detail URL). On the client, the same loader runs on navigation
// — TanStack Query's cache absorbs the duplicate.
async function animeDetailLoader({
  params,
  request,
}: LoaderFunctionArgs): Promise<{
  initialData: unknown;
  initialInfo: unknown;
  host: string;
} | null> {
  const id = Number(params.id);
  const url = new URL(request.url);
  const host = url.host;
  if (!Number.isFinite(id)) {
    return { initialData: undefined, initialInfo: undefined, host };
  }
  try {
    const res = await fetch(`${url.origin}/api/anime/${id}`, {
      headers: { accept: 'application/json' },
      signal: request.signal,
    });
    if (!res.ok) {
      return { initialData: undefined, initialInfo: undefined, host };
    }
    const body = (await res.json()) as { error?: string } | Record<string, unknown>;
    if (body && typeof body === 'object' && 'error' in body) {
      return { initialData: undefined, initialInfo: undefined, host };
    }
    // /anime/:id consumes `initialData` (raw Anime from Yozora);
    // /watch/:id consumes `initialInfo` (toLegacyMedia-converted to
    // the AniList shape its TypeScript expects). Same payload, two
    // shapes so both pages share this loader without renaming props.
    let initialInfo: unknown = body;
    try {
      const { toLegacyMedia } = await import(
        '@v4/shared/lib/yozora/adapters'
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialInfo = toLegacyMedia(body as any);
    } catch {
      // If the adapter fails for any reason, fall through with raw
      // body — the watch page's useEffect refetch will normalize.
    }
    return { initialData: body, initialInfo, host };
  } catch {
    return { initialData: undefined, initialInfo: undefined, host };
  }
}

const NotFound = lazy(
  () => import('./pages/404') as Promise<{ default: ComponentType }>
);

const PublicProfilePage = lazy(
  () =>
    import('./pages/u/[username]') as Promise<{ default: ComponentType }>
);

// RR7's path matcher treats single-segment patterns like `@:username`
// or `@*` as literal text — it doesn't tokenize a literal prefix
// inside a segment. So `/@foo` falls through to the catch-all `*`
// route below. This dispatcher reads the actual pathname: if it
// starts with `/@`, render the public profile page; otherwise show
// the standard 404. Lets us keep the Twitter/Mastodon-style URL with
// no additional route table contortions.
function CatchAllRoute(): ReactElement {
  const location = useLocation();
  if (location.pathname.startsWith('/@')) {
    return <PublicProfilePage />;
  }
  return <NotFound />;
}

function Redirect({ to }: { to: string }): React.ReactElement {
  return <Navigate to={to} replace />;
}

export const routes: RouteObject[] = [
  {
    path: '/',
    Component: App,
    children: [
      { index: true, element: page(() => import('./pages/index')) },
      { path: 'discover', element: page(() => import('./pages/discover')) },
      { path: 'ai', element: page(() => import('./pages/ai')) },
      {
        path: 'changelogs',
        element: page(() => import('./pages/changelogs')),
      },
      { path: 'music', element: page(() => import('./pages/music')) },
      { path: 'now', element: page(() => import('./pages/now')) },
      { path: 'search', element: page(() => import('./pages/search')) },

      { path: 'settings', element: page(() => import('./pages/settings')) },
      {
        path: 'settings/profile',
        element: page(() => import('./pages/settings/profile')),
      },

      {
        path: 'anime/:id',
        element: page(() => import('./pages/anime/[id]')),
        loader: animeDetailLoader,
      },
      {
        path: 'manga/:id',
        element: page(() => import('./pages/manga/[id]')),
      },
      {
        path: 'watch/:id',
        element: page(() => import('./pages/watch/[id]')),
        // Reuse the anime-detail loader: the watch page already
        // accepts the same { initialData, host } props. With this
        // wired up the page's `info` state is hydrated on the very
        // first render, eliminating the WatchSkeleton flash during
        // client-side navigation.
        loader: animeDetailLoader,
      },

      {
        // Backward-compat alias — old links to /u/:username keep
        // resolving. The new canonical /@:username is handled by the
        // CatchAllRoute below since RR7's matcher can't tokenize a
        // literal `@` prefix inside a path segment.
        path: 'u/:username',
        element: page(() => import('./pages/u/[username]')),
      },
      {
        path: 'user/:pathname',
        element: page(() => import('./pages/user/[pathname]')),
      },
      { path: 'users', element: page(() => import('./pages/users/index')) },

      {
        path: 'auth/:pathname',
        element: page(() => import('./pages/auth/[pathname]')),
      },

      // ─── /admin/* (revamp v2) ───────────────────────────────────────
      // App.tsx flips to the lean AdminLayout when pathname starts with /admin.
      { path: 'admin', element: page(() => import('./pages/admin/index')) },
      {
        path: 'admin/catalog',
        element: page(() => import('./pages/admin/catalog')),
      },
      {
        path: 'admin/publishing',
        element: page(() => import('./pages/admin/publishing')),
      },
      {
        path: 'admin/users',
        element: page(() => import('./pages/admin/users')),
      },
      {
        path: 'admin/appearance',
        element: page(() => import('./pages/admin/appearance')),
      },
      {
        path: 'admin/system',
        element: page(() => import('./pages/admin/system')),
      },
      {
        path: 'admin/emergency',
        element: page(() => import('./pages/admin/emergency')),
      },

      // ─── Legacy redirects (60-day window from 2026-05-25) ──────────
      { path: 'admin/episodes', element: <Redirect to="/admin/catalog" /> },
      {
        path: 'admin/community-collections',
        element: <Redirect to="/admin/catalog?tab=collections" />,
      },
      { path: 'admin/changelogs', element: <Redirect to="/admin/publishing" /> },
      {
        path: 'admin/quotes',
        element: <Redirect to="/admin/publishing?tab=quotes" />,
      },
      { path: 'admin/user', element: <Redirect to="/admin/users" /> },
      {
        path: 'admin/badges',
        element: <Redirect to="/admin/users?tab=badges" />,
      },
      { path: 'admin/themes', element: <Redirect to="/admin/appearance" /> },
      { path: 'admin/settings', element: <Redirect to="/admin/system" /> },
      {
        path: 'admin/oauth-clients',
        element: <Redirect to="/admin/system?tab=oauth" /> ,
      },
      {
        path: 'admin/discover-resources',
        element: <Redirect to="/admin/system" />,
      },

      { path: '*', element: <CatchAllRoute /> },
    ],
  },
];
